import { useState, useCallback } from 'react';
import type { CollectionPoint } from '../types';
import {
    fetchCollectionPointsFromDb,
    createCollectionPointInDb,
    updateCollectionPointInDb,
    deleteCollectionPointFromDb,
    type CreateCollectionPointPayload,
    type UpdateCollectionPointPayload,
} from '../services/collectionPointService';
import { getErrorMessage } from '../utils/errors.ts';

export function useCollectionPoints() {
    // Full list of collection points, kept in sync after every mutation.
    const [points,  setPoints]  = useState<CollectionPoint[]>([]);
    // `loading` only covers the initial fetch — mutations are fire-and-return.
    const [loading, setLoading] = useState(false);
    // Holds the most recent error message, or null when the last operation succeeded.
    const [error,   setError]   = useState<string | null>(null);

    // Loads all collection points and replaces local state.
    // Returns the fetched list directly so callers can act on it immediately.
    const fetchPoints = useCallback(async (): Promise<CollectionPoint[]> => {
        setLoading(true);
        setError(null);
        try {
            const data = await fetchCollectionPointsFromDb();
            setPoints(data);
            return data;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to fetch collection points'));
            return [];
        } finally {
            setLoading(false);
        }
    }, []);

    // Inserts a new collection point and appends it to the local list optimistically.
    // Returns the created point on success, or null if the insert failed.
    const createPoint = useCallback(async (
        payload: CreateCollectionPointPayload
    ): Promise<CollectionPoint | null> => {
        setError(null);
        try {
            const created = await createCollectionPointInDb(payload);
            setPoints(prev => [...prev, created]);
            return created;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to create collection point'));
            return null;
        }
    }, []);

    // Patches an existing collection point by ID and updates it in the local list.
    // Returns the updated point on success, or null if the update failed.
    const updatePoint = useCallback(async (
        id: string,
        patch: UpdateCollectionPointPayload
    ): Promise<CollectionPoint | null> => {
        setError(null);
        try {
            const updated = await updateCollectionPointInDb(id, patch);
            setPoints(prev => prev.map(p => p.id === id ? updated : p));
            return updated;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to update collection point'));
            return null;
        }
    }, []);

    // Flips the `is_active` flag on a collection point.
    // Accepts the full point object so the caller doesn't need to track current state.
    // Returns true on success, false if the update failed.
    const toggleActive = useCallback(async (point: CollectionPoint): Promise<boolean> => {
        setError(null);
        try {
            const updated = await updateCollectionPointInDb(point.id, {
                is_active: !point.is_active,
            });
            setPoints(prev => prev.map(p => p.id === point.id ? updated : p));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to toggle collection point'));
            return false;
        }
    }, []);

    // Hard-deletes a collection point by ID and removes it from the local list.
    // Returns true on success, false if the delete failed.
    const deletePoint = useCallback(async (id: string): Promise<boolean> => {
        setError(null);
        try {
            await deleteCollectionPointFromDb(id);
            setPoints(prev => prev.filter(p => p.id !== id));
            return true;
        } catch (err) {
            setError(getErrorMessage(err, 'Failed to delete collection point'));
            return false;
        }
    }, []);

    return {
        points,
        loading,
        error,
        fetchPoints,
        createPoint,
        updatePoint,
        toggleActive,
        deletePoint,
    };
}